import { useCallback, useState } from 'react';

export interface CursorPage<T> {
  items: T[];
  page: { next_cursor?: string | null; has_more: boolean; total?: number | null; as_of?: string | null };
}

export const DEFAULT_PAGE_SIZE = 50;

export interface CursorPager {
  /** Cursor to request for the current page (undefined on the first page). */
  cursor: string | undefined;
  pageIndex: number;
  pageSize: number;
  canPrev: boolean;
  next: (nextCursor: string | null | undefined) => void;
  prev: () => void;
  restart: () => void;
}

/**
 * Opaque cursor chain for server pagination. Any change to `queryKey` (filters,
 * sort, tenant) restarts at the first page; pages are never numbered beyond what
 * the API proves.
 */
export function useCursorPager(queryKey: string, initialPageSize = DEFAULT_PAGE_SIZE): CursorPager {
  const [state, setState] = useState<{ key: string; stack: string[] }>({ key: queryKey, stack: [] });
  let current = state;
  if (state.key !== queryKey) {
    current = { key: queryKey, stack: [] };
    setState(current);
  }
  const next = useCallback((nextCursor: string | null | undefined) => {
    if (!nextCursor) return;
    setState((s) => ({ ...s, stack: [...s.stack, nextCursor] }));
  }, []);
  const prev = useCallback(() => setState((s) => ({ ...s, stack: s.stack.slice(0, -1) })), []);
  const restart = useCallback(() => setState((s) => ({ ...s, stack: [] })), []);
  return {
    cursor: current.stack[current.stack.length - 1],
    pageIndex: current.stack.length,
    pageSize: initialPageSize,
    canPrev: current.stack.length > 0,
    next,
    prev,
    restart,
  };
}

export interface SortEntry {
  /** Allowlisted API sort key. */
  key: string;
  desc: boolean;
}

export const MAX_SORT_KEYS = 3;

/** `sort=-updated_at,name` per docs/api-contract.md section 2. The server appends the id tie breaker. */
export function toSortParam(sort: readonly SortEntry[]): string | undefined {
  if (sort.length === 0) return undefined;
  return sort.map((s) => `${s.desc ? '-' : ''}${s.key}`).join(',');
}
